import { CookiesService, provideUtilsCookies } from '@noda-lib/utils/cookies';
import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, Injectable } from '@angular/core';
import { WINDOW } from '@noda-lib/utils/injection';
import { Subject, of, filter, map, take } from 'rxjs';
import { getDataFromDB } from '@noda-lib/utils/db';
import { withSsrCookies } from '@noda-lib/utils/ssr';
const ABTestingDB = {
  script: accountId => {
    return `
      window._vwo_code || (function() {
      var account_id=${accountId},
      version=2.1,
      settings_tolerance=2000,
      hide_element='body',
      hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;transition:none !important;',
      /* DO NOT EDIT BELOW THIS LINE */
      f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={nonce:v&&v.nonce,use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}v&&t.setAttribute('nonce',v.nonce);d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var n=this.getSettings(),i=d.createElement('script'),r=this;t=t||{};if(n){i.textContent=n;d.getElementsByTagName('head')[0].appendChild(i);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200||o.status===304){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var n=this.hide_element(),i=n?n+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=i;else t.appendChild(d.createTextNode(i));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var i=d.createElement('div');i.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;display: block !important;';i.setAttribute('id','_vis_opt_path_hides');i.classList.add('_vis_hide_layer');t.parentNode.insertBefore(i,t.nextSibling)}var o=window._vis_opt_url||d.URL,s='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(o)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:s})}else{this.load(s+'&x=true')}}};w._vwo_code=code;code.init();})();
    `;
  }
};
let ABTestingService = /*#__PURE__*/(() => {
  class ABTestingService {
    constructor(rendererFactory) {
      this.renderer = null;
      this.appliedVariation$ = new Subject();
      this.cookiesService = inject(CookiesService);
      this.document = inject(DOCUMENT);
      this.window = inject(WINDOW);
      this.renderer = rendererFactory.createRenderer(null, null);
    }
    init(accountId) {
      this.insertLinkIntoHead();
      this.insertScriptIntoHead(accountId);
      this.setupVWO();
      this.setupEvent();
      this.setupVariationListener();
    }
    callEvent(name, params = {}) {
      try {
        this.window.VWO.event(name, params);
      } catch (error) {
        console.error('Setup VWO first!', error);
      }
    }
    /**
     * Получает текущую вариацию кампании A/B-теста на основе заданного номера кампании.
     * Кампания -- это A/B-тест в утилите VWO, которую мы используем для проведения тестирования. Каждая компания
     * имеет свой порядковый номер, который является ее идентификатором.
     *
     * Этот метод сначала пытается получить вариацию для отладки из cookie с именем `debug_vis_opt_exp_${campaignNumber}_combi`.
     * Если такой cookie нет, метод возвращает вариацию из основного cookie `_vis_opt_exp_${campaignNumber}_combi`.
     *
     * @param {string} campaignNumber - Номер кампании, для которой нужно получить текущую вариацию.
     * @returns {string} Текущая вариация кампании.
     */
    getVariation(campaignNumber) {
      const debuggingVariation = this.cookiesService.get(`debug_vis_opt_exp_${campaignNumber}_combi`);
      const variation = this.cookiesService.get(`_vis_opt_exp_${campaignNumber}_combi`);
      return debuggingVariation || variation;
    }
    /**
     * Отправляет ивент в VWO по которому запускается кампания для пользователя
     *
     * Нужно для тех кампаний, где активация присходит не на старте приложения
     * и зависит от условий или должна применяться для определенной группы пользователей
     * После возврата значения Observable комплитится
     *
     * @param {string} trigger - имя триггера по которому запускается кампания
     * @param {string} campaignId - номер кампании
     * @param {object} payload - данные, которые передаются в триггер
     *
     * @returns {Observable<string>} активный вариант кампании
     */
    activateCampaign(trigger, campaignId, payload = {}) {
      this.callEvent(trigger, payload);
      return this.onCampaignActivated(campaignId);
    }
    /**
     * Позволяет подписаться на активацию кампании, которая зависит от внешнего триггера
     * После возврата значения Observable комплитится
     *
     * @param {string} campaignId - номер кампании
     *
     * @returns {Observable<string>} - активный вариант кампании
     */
    onCampaignActivated(campaignId) {
      const activeVariation = this.getVariation(campaignId);
      if (activeVariation) {
        return of(activeVariation);
      }
      return this.appliedVariation$.pipe(filter(activeVariation => activeVariation.campaignId == campaignId), map(activeVariation => activeVariation.variationId), take(1));
    }
    insertLinkIntoHead() {
      const linkElement = this.renderer.createElement('link');
      this.renderer.setAttribute(linkElement, 'rel', 'preconnect');
      this.renderer.setAttribute(linkElement, 'href', 'https://dev.visualwebsiteoptimizer.com');
      this.renderer.insertBefore(this.document.head, linkElement, this.document.head.firstChild);
    }
    insertScriptIntoHead(accountId) {
      const scriptElement = this.createScript(accountId);
      this.renderer.insertBefore(this.document.head, scriptElement, this.document.head.firstChild);
    }
    createScript(accountId) {
      const script = getDataFromDB(ABTestingDB, ['script']);
      const scriptElement = this.renderer.createElement('script');
      const textNode = this.renderer.createText(script(accountId));
      this.renderer.setAttribute(scriptElement, 'type', 'text/javascript');
      this.renderer.setAttribute(scriptElement, 'id', 'vwoCode');
      this.renderer.appendChild(scriptElement, textNode);
      return scriptElement;
    }
    setupVWO() {
      const {
        window
      } = this;
      window.VWO = window.VWO || [];
    }
    setupEvent() {
      const {
        window
      } = this;
      // Do not change anything in the following lines:
      window.VWO.event = window.VWO.event || function () {
        // eslint-disable-next-line prefer-rest-params
        window.VWO.push(['event'].concat([].slice.call(arguments)));
      };
    }
    setupVariationListener() {
      const {
        window,
        appliedVariation$
      } = this;
      window.VWO.push(['onVariationApplied', function (state) {
        if (!state) return;
        const [campaignId, variationId] = state.slice(1);
        appliedVariation$.next({
          campaignId,
          variationId
        });
      }]);
    }
    static {
      this.ɵfac = function ABTestingService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ABTestingService)(i0.ɵɵinject(i0.RendererFactory2));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ABTestingService,
        factory: ABTestingService.ɵfac
      });
    }
  }
  return ABTestingService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function provideABTesting(config) {
  const cookieProviders = config?.ssrEnabled ? provideUtilsCookies(withSsrCookies()) : provideUtilsCookies();
  return [cookieProviders, ABTestingService];
}

/**
 * Generated bundle index. Do not edit.
 */

export { ABTestingService, provideABTesting };
